import { Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Button } from 'primeng/button';
import { Popover } from 'primeng/popover';
import { RELEASE_NOTES } from 'app/modules/secure/apps/edm/docs/changelog/release-notes-data';

interface ServiceVersion {
    slug: string;
    label: string;
    version: string;
}

// Deliberate display order -- UI first, then the shared domain-model/ontology layer, then backend
// microservices roughly in the order a request flows through them -- rather than whatever order
// slugs happen to first appear in RELEASE_NOTES.
const DISPLAY_ORDER = [
    'knightfly-ui',
    'sdf-kg-domainmodel',
    'sdf-kg-ontology-datamodel',
    'sdf-kg-ekc-service',
    'sdf-kg-lds-service',
    'sdf-kg-du-service',
    'sdf-kg-dqm-service',
    'sdf-kg-dq-remediation-service',
    'sdf-kg-nurv-ai-service',
    'sdf-kg-harvestor-service',
    'sdf-kg-edm-gateway-server',
    'sdf-kg-edm-eureka-server',
];

// RELEASE_NOTES is newest-first overall (each release prepends its own entry at the top of the
// array), so the first entry matching a given slug is that service's current version.
function latestVersionsBySlug(): Map<string, ServiceVersion> {
    const latest = new Map<string, ServiceVersion>();
    for (const entry of RELEASE_NOTES) {
        if (!latest.has(entry.slug)) {
            latest.set(entry.slug, { slug: entry.slug, label: entry.serviceLabel, version: entry.version });
        }
    }
    return latest;
}

/** Small footer widget showing the UI's own version, with a popover listing the current version
 *  of every backend microservice and the shared domain-model/ontology repos -- kept in sync with
 *  release-notes-data.ts, the same file already updated by hand after every release cut. */
@Component({
    selector: 'app-release-versions',
    imports: [RouterLink, Button, Popover],
    templateUrl: './release-versions.component.html',
})
export class ReleaseVersionsComponent {
    private readonly _latest = latestVersionsBySlug();

    readonly knightflyVersion = this._latest.get('knightfly-ui')?.version ?? '';

    readonly versions: ServiceVersion[] = DISPLAY_ORDER.filter((slug) =>
        this._latest.has(slug),
    ).map((slug) => this._latest.get(slug)!);
}
